import React from "react";

import { OpView } from "../../../../assets/icons";
import { ChainSingleSelection } from "../../../../components/ChainSelection";
import { Box, Checkbox } from "../../../../components/MuiGenerals";
import OpIconButton from "../../../../components/OpIconButton";
import { useTranslation } from "../../../../hooks";
import { genField } from "../../../../utils/HelperComp";
import { customSx } from "../../../../utils/styling";
import { EXACT_PAY_MAX_SLIPPAGE_MAP } from "../constants/slippage";
import FromToCurrencyDirection from "../Content/components/FromToCurrencyDirection";
import MaxSlippageField from "../Content/components/MaxSlippageField";
import { useCreateSwapRequestContext } from "../context";
import { initFilterFields } from "../context/CreateSwapRequestContext";
import { getTokensFromCurrencyPair } from "../helpers/getTokensFromCurrencyPair";
import { useCurrencyDirection } from "../hooks";
import { ExactPaySlippageTypes, FilterFields } from "../types";
import CurrencyPairSingleSelection from "./CurrencyPairSingleSelection";
import SwapPathResponseDialogForQA from "./SwapPathResponseDialogForQA";
import { useChains } from "src/components/FetchConfig";
import { enumChainIdToType } from "src/utils/constant";

interface IProps {
  fields: FilterFields;
  setFields: (fields: FilterFields) => void;
  onlyHaveOneTab?: boolean;
}

const Filters = (props: IProps) => {
  const { fields, setFields, onlyHaveOneTab } = props;

  const [isSwapResponseDialogOpen, setIsSwapResponseDialogOpen] =
    React.useState(false);

  const {
    resetContent,
    resetQuoteDetails,
    maxSlippageTabId,
    setMaxSlippageTabId,
    activeTabId,
  } = useCreateSwapRequestContext();

  const { t } = useTranslation("createSwapRequest");

  const { handleSwitchCurrency } = useCurrencyDirection({
    currencyPair: fields.currencyPair,
    onCurrencyChange: (fromCurrency: string, toCurrency: string) => {
      setFields({
        ...fields,
        fromCurrency,
        toCurrency,
      });
    },
  });

  const { selectChainByPieces } = useChains();

  const { chain_type } = selectChainByPieces({ chain_name: fields.chainName });

  const isTron = enumChainIdToType.TRON === String(chain_type);

  const filters = genField({ t }, [
    [
      "chainName",
      <ChainSingleSelection
        label={t("psChainName")}
        setChoice={(chainName: string) => {
          setFields({
            ...initFilterFields,
            chainName,
            currencyPair: "",
          });
          resetContent();
        }}
        choice={fields.chainName}
        onlyEthAndTron={true}
      />,
    ],
    [
      "currencyPair",
      <CurrencyPairSingleSelection
        label={t("psCurrencyPair")}
        value={fields.currencyPair}
        chainName={fields.chainName}
        onChange={(e) => {
          const currencyPairArr = getTokensFromCurrencyPair(e.target.value);
          setFields({
            ...fields,
            currencyPair: e.target.value,
            fromCurrency: currencyPairArr[0],
            toCurrency: currencyPairArr[1],
          });
          resetQuoteDetails();
        }}
        onClear={() => {}}
      />,
    ],
    [
      "maxSlippage",
      <div
        style={{
          paddingTop: "14px",
          maxWidth: "80%",
          minWidth: "180px",
        }}
      >
        <MaxSlippageField
          activeTabId={maxSlippageTabId}
          onTabClick={(tabId: string) => {
            setMaxSlippageTabId(tabId as ExactPaySlippageTypes);
            setFields({
              ...fields,
              maxSlippage: String(
                EXACT_PAY_MAX_SLIPPAGE_MAP[tabId as ExactPaySlippageTypes]
              ),
            });

            resetQuoteDetails();
          }}
        />
      </div>,
    ],
    [
      "direction",
      <div style={{ paddingTop: "6px" }}>
        <FromToCurrencyDirection
          fromCurrency={fields.fromCurrency}
          toCurrency={fields.toCurrency}
          onSwitch={() => {
            handleSwitchCurrency();
            resetQuoteDetails();
          }}
        />
      </div>,
    ],
    [
      "swapPathResponse",
      <div style={{ paddingTop: "14px" }}>
        <OpIconButton
          title={t("view")}
          url={OpView}
          onClick={() => {
            setIsSwapResponseDialogOpen(true);
          }}
          isDisabled={!fields.chainName || !fields.currencyPair}
        />
      </div>,
    ],
    [
      "tronRent",
      <div style={{ paddingTop: "4px" }}>
        <Checkbox
          checked={fields.isTronRent}
          sx={{ "& .MuiSvgIcon-root": { fontSize: 26 } }}
          onChange={(e) =>
            setFields({
              ...fields,
              isTronRent: e.target.checked,
            })
          }
        />
      </div>,
    ],
  ]);
  return (
    <>
      <SwapPathResponseDialogForQA
        isOpen={isSwapResponseDialogOpen}
        onClose={() => {
          setIsSwapResponseDialogOpen(false);
        }}
      />
      <Box sx={customSx.gridFilter} className="gridFilter">
        {Object.entries(filters).map(([key, value]: any) => {
          if (key === "tronRent" && !isTron) {
            return undefined;
          }

          return <React.Fragment key={key}>{value}</React.Fragment>;
        })}
      </Box>
    </>
  );
};

export default Filters;
